import window from 'window-or-global';

import { getUrlForDomainInContext, isFedRamp as isFedRampAtlContext, isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';

import { getAppName } from '../app';
import { getEnv } from '../env';
import { LOCAL_PRODUCTION_BACKEND } from '../globals';

export interface Config {
	stargateRoot: string;
	analytics: {
		scheme: string;
		server: string;
	};
	feedbackIssueCollectorId: string;
	atlassianAccountUrl: string;
	permsServiceUrl: string;
	collaborationGraph?: string;
	emojiUrl: string;
	teamCentralDisabled?: boolean;
	teamCentralUrl?: string;
}

// default configs is also config for production env
const productionConfig: Config = {
	stargateRoot: '/gateway/api',
	analytics: {
		scheme: 'https',
		server: 'analytics.atlassian.com',
	},
	feedbackIssueCollectorId: '908df207',
	atlassianAccountUrl: 'https://id.atlassian.com',
	permsServiceUrl: '/gateway/api/permissions',
	collaborationGraph: '/gateway/api/collaboration',
	emojiUrl: '/gateway/api/emoji/standard',
	teamCentralUrl: 'https://team.atlassian.com',
};

const devAndStagingConfig: Config = {
	stargateRoot: '/gateway/api',
	analytics: {
		scheme: 'https',
		server: 'analytics.atlassian.com',
	},
	feedbackIssueCollectorId: '908df207',
	atlassianAccountUrl: 'https://id.stg.internal.atlassian.com',
	permsServiceUrl: '/gateway/api/permissions',
	collaborationGraph: '/gateway/api/collaboration',
	emojiUrl: '/gateway/api/emoji/standard',
	teamCentralUrl: 'https://team.stg.atlassian.com',
};

const localAndTestConfig: Config = {
	stargateRoot: '/gateway/api',
	analytics: {
		scheme: 'http',
		server: 'localhost:3000',
	},
	feedbackIssueCollectorId: '908df207',
	atlassianAccountUrl: 'https://id.stg.internal.atlassian.com',
	permsServiceUrl: '/gateway/api/permissions',
	collaborationGraph: '/gateway/api/collaboration',
	emojiUrl: '/gateway/api/emoji/standard',
	teamCentralUrl: 'https://team.stg.atlassian.com',
};

const isolatedCloudConfig: Config = {
	stargateRoot: '/gateway/api',
	analytics: {
		scheme: 'https',
		server: 'analytics.atlassian.com',
	},
	feedbackIssueCollectorId: '908df207',
	// Note that the env type passed to getUrlForDomainInContext (e.g. prod) doesn't matter
	// in Isolated Cloud, so this will work for staging IC too
	atlassianAccountUrl: getUrlForDomainInContext('id', 'prod') ?? 'id.atlassian.com',
	permsServiceUrl: '/gateway/api/permissions',
	collaborationGraph: '/gateway/api/collaboration',
	emojiUrl: '/gateway/api/emoji/standard',
	teamCentralUrl: `https://team.${isolatedCloudDomain() ?? ''}`,
}

const fedrampConfig: Config = {
	stargateRoot: '/gateway/api',
	analytics: {
		scheme: 'https',
		server: 'analytics.atlassian.com',
	},
	feedbackIssueCollectorId: '908df207',
	atlassianAccountUrl: 'https://id.atlassian-us-gov-mod.com',
	permsServiceUrl: '/gateway/api/permissions',
	collaborationGraph: '/gateway/api/collaboration',
	emojiUrl: '/gateway/api/emoji/standard',
	teamCentralDisabled: true,
};

const EnvOverrideKeys = ['adev', 'stg-east', 'local', 'test', 'fedramp', 'prod-east'] as const;
type EnvOverrideKey = (typeof EnvOverrideKeys)[number];
const environmentOverrides: Record<EnvOverrideKey, Config> = {
	'prod-east': productionConfig,
	local: {
		...(LOCAL_PRODUCTION_BACKEND ? productionConfig : localAndTestConfig),
		analytics: {
			scheme: 'http',
			server: typeof window !== 'undefined' ? `${window.location.hostname}:3000` : 'localhost:3000',
		},
	},
	test: {
		...localAndTestConfig,
	},
	adev: devAndStagingConfig,
	'stg-east': devAndStagingConfig,
	fedramp: fedrampConfig,
};

const cachedConfig: Record<string, Config> = {};

export function isFedramp(): boolean {
	return isFedRampAtlContext();
}

export function getConfig(): Config {
	const env = isFedramp() ? 'fedramp' : getEnv();

	const appName = getAppName();
	const key = `${env}_${appName}`;
	if (!cachedConfig[key]) {
		if (isIsolatedCloud()) {
			cachedConfig[key] = isolatedCloudConfig;
		} else if (EnvOverrideKeys.includes(env as any)) {
			cachedConfig[key] = { ...environmentOverrides[env as EnvOverrideKey] };
		} else {
			// fallback to prod as customers can have custom domains.
			cachedConfig[key] = { ...environmentOverrides['prod-east'] };
		}
	}
	return cachedConfig[key];
}
